// Accommodation Service for Firestore Operations
import {
    collection,
    doc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    serverTimestamp,
    Timestamp,
    onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Accommodation, AccommodationType } from '../models/index';

export class AccommodationService {
    private static getCollectionPath(tripId: string) {
        return `trips/${tripId}/accommodations`;
    }

    private static convertTimestamps(data: any): any {
        const converted = { ...data };
        const toDate = (v: any): Date | undefined => {
            if (v instanceof Timestamp) return v.toDate();
            if (typeof v === 'string') { const d = new Date(v); if (!isNaN(d.getTime())) return d; }
            return undefined;
        };
        for (const field of ['checkInDate', 'checkOutDate', 'cancelUntil', 'cancellationDeadline', 'createdAt', 'updatedAt']) {
            const d = toDate(data[field]);
            if (d) converted[field] = d;
        }
        return converted;
    }

    // Get all accommodations for a trip
    static async getAccommodations(tripId: string): Promise<Accommodation[]> {
        try {
            console.log('🔍 [AccommodationService] Getting accommodations for trip:', tripId);
            const accommodationsRef = collection(db, this.getCollectionPath(tripId));
            const snapshot = await getDocs(accommodationsRef);

            console.log('✅ [AccommodationService] Found', snapshot.size, 'accommodations');
            const accommodations = snapshot.docs.map((doc, index) => {
                const data = doc.data();
                const accommodation = {
                    ...this.convertTimestamps(data),
                    id: doc.id,  // always use Firestore doc ID, not any id stored in data
                    tripId,
                } as Accommodation;

                // Normalise address: email-created accommodations store it inside
                // location.address (mobile format) rather than at the top level.
                if (!accommodation.address && data.location?.address) {
                    accommodation.address = data.location.address;
                }

                // Normalise free cancellation: mobile stores only cancelUntil,
                // web uses freeCancellation + cancellationDeadline.
                if (accommodation.cancelUntil && !accommodation.cancellationDeadline) {
                    accommodation.cancellationDeadline = accommodation.cancelUntil;
                }
                if (accommodation.cancelUntil && accommodation.freeCancellation === undefined) {
                    (accommodation as any).freeCancellation = true;
                }

                // Debug: Log all fields for first accommodation to see structure
                if (index === 0) {
                    console.log('🔍 [AccommodationService] First accommodation fields:', Object.keys(data));
                    console.log('🔍 [AccommodationService] First accommodation sample:', {
                        name: data.name,
                        location: data.location,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        address: accommodation.address,
                    });
                }

                // Debug: Log location data
                if (data.location) {
                    console.log('📍 [AccommodationService] Accommodation location:', accommodation.name || accommodation.id, data.location);
                } else if (data.latitude && data.longitude) {
                    console.log('📍 [AccommodationService] Accommodation has lat/lng (no location object):', accommodation.name || accommodation.id, { lat: data.latitude, lng: data.longitude });
                }

                return accommodation;
            });

            // Sort in memory by checkInDate
            accommodations.sort((a, b) => {
                const dateA = new Date(a.checkInDate || 0).getTime();
                const dateB = new Date(b.checkInDate || 0).getTime();
                return dateA - dateB;
            });

            return accommodations;
        } catch (error) {
            console.error('❌ [AccommodationService] Error getting accommodations:', error);
            throw error;
        }
    }

    // Subscribe to accommodations
    static subscribeToAccommodations(
        tripId: string,
        callback: (accommodations: Accommodation[]) => void
    ): () => void {
        console.log('🔍 [AccommodationService] Setting up accommodation subscription for trip:', tripId);
        const accommodationsRef = collection(db, this.getCollectionPath(tripId));

        return onSnapshot(
            accommodationsRef,
            (snapshot) => {
                console.log('📡 [AccommodationService] Accommodation update:', snapshot.size, 'accommodations');
                const accommodations = snapshot.docs.map((doc, index) => {
                    const data = doc.data();
                    const accommodation = {
                        ...this.convertTimestamps(data),
                        id: doc.id,  // always use Firestore doc ID, not any id stored in data
                        tripId,
                    } as Accommodation;

                    // Normalise address: email-created accommodations store it inside
                    // location.address (mobile format) rather than at the top level.
                    if (!accommodation.address && data.location?.address) {
                        accommodation.address = data.location.address;
                    }

                    // Normalise free cancellation: mobile stores only cancelUntil,
                    // web uses freeCancellation + cancellationDeadline.
                    if (accommodation.cancelUntil && !accommodation.cancellationDeadline) {
                        accommodation.cancellationDeadline = accommodation.cancelUntil;
                    }
                    if (accommodation.cancelUntil && accommodation.freeCancellation === undefined) {
                        (accommodation as any).freeCancellation = true;
                    }

                    // Debug: Log all fields for first accommodation
                    if (index === 0) {
                        console.log('🔍 [AccommodationService] First accommodation fields:', Object.keys(data));
                        console.log('🔍 [AccommodationService] First accommodation sample:', {
                            name: data.name,
                            location: data.location,
                            latitude: data.latitude,
                            longitude: data.longitude,
                            address: accommodation.address,
                        });
                    }

                    // Debug: Log location data
                    if (data.location) {
                        console.log('📍 [AccommodationService] Accommodation HAS location:', accommodation.name || accommodation.id, data.location);
                    } else if (data.latitude && data.longitude) {
                        console.log('📍 [AccommodationService] Accommodation has lat/lng:', accommodation.name || accommodation.id, { lat: data.latitude, lng: data.longitude });
                    }

                    return accommodation;
                });

                // Sort in memory by checkInDate
                accommodations.sort((a, b) => {
                    const dateA = new Date(a.checkInDate || 0).getTime();
                    const dateB = new Date(b.checkInDate || 0).getTime();
                    return dateA - dateB;
                });

                callback(accommodations);
            },
            (error) => {
                console.error('❌ [AccommodationService] Error in accommodation subscription:', error);
            }
        );
    }

    // Create accommodation
    static async createAccommodation(
        tripId: string,
        accommodation: Omit<Accommodation, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>
    ): Promise<string> {
        try {
            const accommodationRef = doc(collection(db, this.getCollectionPath(tripId)));
            // Strip undefined fields — Firestore rejects them with "Unsupported field value: undefined"
            const accommodationData = Object.fromEntries(
                Object.entries({
                    ...accommodation,
                    tripId,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                }).filter(([, v]) => v !== undefined)
            );
            await setDoc(accommodationRef, accommodationData);
            return accommodationRef.id;
        } catch (error) {
            console.error('Error creating accommodation:', error);
            throw error;
        }
    }

    // Update accommodation
    static async updateAccommodation(
        tripId: string,
        accommodationId: string,
        updates: Partial<Accommodation>
    ): Promise<void> {
        try {
            const accommodationRef = doc(db, this.getCollectionPath(tripId), accommodationId);
            const updateData = Object.fromEntries(
                Object.entries({
                    ...updates,
                    updatedAt: serverTimestamp(),
                }).filter(([, v]) => v !== undefined)
            );
            await updateDoc(accommodationRef, updateData);
        } catch (error) {
            console.error('Error updating accommodation:', error);
            throw error;
        }
    }

    // Delete accommodation
    static async deleteAccommodation(tripId: string, accommodationId: string): Promise<void> {
        try {
            await deleteDoc(doc(db, this.getCollectionPath(tripId), accommodationId));
        } catch (error) {
            console.error('Error deleting accommodation:', error);
            throw error;
        }
    }

    // Check for overlapping dates
    static hasOverlappingDates(
        accommodations: Accommodation[],
        checkIn: Date,
        checkOut: Date,
        excludeId?: string
    ): boolean {
        return accommodations.some((acc) => {
            if (excludeId && acc.id === excludeId) return false;
            if (!acc.checkInDate || !acc.checkOutDate) return false;

            const accCheckIn = new Date(acc.checkInDate);
            const accCheckOut = new Date(acc.checkOutDate);

            return (
                (checkIn >= accCheckIn && checkIn < accCheckOut) ||
                (checkOut > accCheckIn && checkOut <= accCheckOut) ||
                (checkIn <= accCheckIn && checkOut >= accCheckOut)
            );
        });
    }

    // Get accommodation for a specific date
    static getAccommodationForDate(accommodations: Accommodation[], date: Date): Accommodation | null {
        return accommodations.find((acc) => {
            if (!acc.checkInDate || !acc.checkOutDate) return false;

            const checkIn = new Date(acc.checkInDate);
            const checkOut = new Date(acc.checkOutDate);

            return date >= checkIn && date < checkOut;
        }) || null;
    }

    // Calculate total accommodation cost
    static calculateTotalCost(accommodations: Accommodation[], currency?: string): number {
        return accommodations.reduce((total, acc) => {
            if (currency && acc.currency !== currency) return total;
            return total + acc.price;
        }, 0);
    }
}

export default AccommodationService;
